import { PickSampleExt } from "@novorender/api";
import { vec2, vec3 } from "gl-matrix";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { readyPdfObjectIds } from "contexts/explorerGlobals/reducer";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { isPdfOrDrawingObjectId, type SelectionTarget } from "dataAccess";
import { undoRedoActions } from "features/undoRedo";
import {
    selectCanvasContextMenuFeatures,
    selectIsProcoreDesign,
    selectNodeClassSelectionEnabled,
    selectSelectionResolution,
} from "slices/explorer";

import { getPlaneCreationNormal } from "../planeCreationNormal";
import {
    renderActions,
    selectCameraType,
    selectCanvasSelectionEnabled,
    selectClippingPlanes,
    selectIsXRayActive,
    selectMainObject,
    selectMainObjects,
    selectPicker,
    selectSelectMultiple,
} from "../renderSlice";
import { resolveSelectionTarget } from "../selectionResolution";
import { sheetScenePickIsBlocked } from "../sheetSceneSelection";
import { CameraType, Picker, Stamp, StampKind } from "../types";
import {
    beginOptimisticPaintAttempt,
    discardPendingOptimisticPaint,
    getPendingOptimisticPaint,
    isCurrentOptimisticPaintOwner,
    resetOptimisticPaintState,
    setPendingOptimisticPaint,
} from "./sharedOptimisticPaint";
import { abortSharedSelection } from "./sharedSelectionAbort";

export { discardPendingOptimisticPaint as discardPendingContextMenuPaint } from "./sharedOptimisticPaint";

/** Test-only: drop leftover generation / paint snapshot from a previous case. */
export function resetContextMenuHandlerState(): void {
    resetOptimisticPaintState();
}

export function useCanvasContextMenuHandler() {
    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const highlightedObjects = useHighlighted();
    const cameraType = useAppSelector(selectCameraType);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const features = useAppSelector(selectCanvasContextMenuFeatures);
    const picker = useAppSelector(selectPicker);
    const isMultipleSelectionActive = useAppSelector(selectSelectMultiple);
    const {
        state: { view, canvas, dataAccess, pdfObjectDiscovery },
    } = useExplorerGlobals();

    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const canvasSelectionEnabled = useAppSelector(selectCanvasSelectionEnabled);
    // X-ray is read off the store rather than selected: both entry points can run after
    // an await — the pick, the PDF check and the resolve here, and the left-click's own
    // awaits before it calls showContextMenuStamp — so a value captured on render is
    // stale by then and an isolation that turns X-ray on mid-flight would lose to it.
    const store = useAppStore();
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const nodeClassSelectionEnabled = useAppSelector(selectNodeClassSelectionEnabled);

    const shouldShow = isProcoreDesign ? canvasSelectionEnabled : true;

    const buildStampForResult = (
        pos: vec2,
        result: PickSampleExt | undefined,
        objectId = result && result.objectId !== -1 ? result.objectId : undefined,
    ): Stamp | null => {
        if (!canvas) {
            return null;
        }
        const canvasRect = canvas.getBoundingClientRect();
        const normal = result ? getPlaneCreationNormal(result.normal) : undefined;

        if (!result || result.objectId === -1) {
            if (cameraType === CameraType.Orthographic && clippingPlanes.planes.length) {
                return {
                    kind: StampKind.CanvasContextMenu,
                    data: {
                        object: objectId ?? result?.objectId ?? undefined,
                        position: result?.position ?? undefined,
                        normal: normal ? [...normal] : undefined,
                    },
                    pinned: true,
                    mouseX: pos[0] + canvasRect.left,
                    mouseY: pos[1] + canvasRect.top,
                };
            }
            return null;
        }

        return {
            kind: StampKind.CanvasContextMenu,
            data: {
                object: objectId,
                position: [...result.position] as vec3,
                normal: normal ? [...normal] : undefined,
            },
            pinned: true,
            mouseX: pos[0] + canvasRect.left,
            mouseY: pos[1] + canvasRect.top,
        };
    };

    /**
     * Display the canvas context menu stamp based on an existing pick result,
     * without performing a new pick or modifying the current selection.
     * Used to open the context menu after a pick-selection (e.g. on touch tap).
     */
    const showContextMenuStamp = (pos: vec2, result: PickSampleExt | undefined, objectId?: number) => {
        if (
            !shouldShow ||
            !view ||
            picker !== Picker.Object ||
            !features.length ||
            !canvas ||
            selectIsXRayActive(store.getState())
        ) {
            return;
        }

        const stamp = buildStampForResult(pos, result, objectId);
        if (stamp) {
            dispatch(renderActions.setStamp(stamp));
        }
    };

    const handleContextMenu = async (pos: vec2, isTouch: boolean) => {
        if (
            !shouldShow ||
            !view ||
            picker !== Picker.Object ||
            !features.length ||
            !canvas ||
            selectIsXRayActive(store.getState())
        ) {
            return;
        }

        // Cancel any in-flight left- or right-click resolve before this pick starts,
        // including empty-space / PDF follow-ups that never reach resolveSelectionTarget.
        const abortSignal = abortSharedSelection();
        const stampGeneration = beginOptimisticPaintAttempt();

        const result = await view.pick(pos[0], pos[1], {
            sampleDiscRadius: isTouch ? 8 : 4,
        });

        if (abortSignal.aborted) {
            return;
        }

        if (selectIsXRayActive(store.getState())) {
            // An isolation turned X-ray on while the pick was in flight. The menu is not
            // offered under X-ray, and its selection must survive this click.
            return;
        }

        if (!result || result.objectId === -1) {
            const pendingPaint = getPendingOptimisticPaint();
            if (pendingPaint) {
                dispatch(renderActions.setMainObject(pendingPaint.previousMainObject));
                dispatch(renderActions.setMainObjects(pendingPaint.previousMainObjects));
                dispatchHighlighted(highlightActions.setIds(pendingPaint.previousHighlightIds));
                discardPendingOptimisticPaint();
            }
            const stamp = buildStampForResult(pos, result);
            if (stamp) {
                dispatch(renderActions.setStamp(stamp));
            } else {
                dispatch(renderActions.setStamp(null));
            }
            return;
        }

        // If a previous left- or right-click still has an uncommitted optimistic paint,
        // treat that click's pre-paint selection as the baseline so this click
        // does not see the in-flight fragment as "already selected".
        const pendingPaint = getPendingOptimisticPaint();
        const baselineMainObject = pendingPaint?.previousMainObject ?? mainObject;
        const baselineMainObjects = pendingPaint?.previousMainObjects ?? mainObjects;
        const baselineHighlightIds = pendingPaint?.previousHighlightIds ?? highlightedObjects.idArr;
        const baselineHighlightSet = new Set(baselineHighlightIds);

        // Re-click of a mesh that's already highlighted: the selection list is
        // already known, so open the menu without another First/Last walk.
        if (baselineHighlightSet.has(result.objectId)) {
            // An interrupted click leaves its optimistic fragment painted (its abort
            // path returns without restoring; the interrupting click inherits the
            // snapshot). Restore the pre-paint selection here so the 3D highlights
            // agree with the menu we are about to open for the baseline object.
            if (pendingPaint) {
                dispatch(renderActions.setMainObject(baselineMainObject));
                dispatch(renderActions.setMainObjects(baselineMainObjects));
                dispatchHighlighted(highlightActions.setIds(baselineHighlightIds));
                discardPendingOptimisticPaint();
            }
            const stamp = buildStampForResult(pos, result, baselineMainObject ?? result.objectId);
            if (stamp) {
                dispatch(renderActions.setStamp(stamp));
            }
            return;
        }

        // Match left-click: start First/Last resolve in parallel with the PDF
        // check so only the optimistic paint waits on PDF, not the tree walk.
        const selectionTargetPromise = dataAccess
            ? resolveSelectionTarget({
                  dataAccess,
                  objectId: result.objectId,
                  resolution: selectionResolution,
                  abortSignal,
                  classAware: nodeClassSelectionEnabled,
              })
            : undefined;

        const knownPdfObjectIds = readyPdfObjectIds(pdfObjectDiscovery);
        const pickedSheet =
            isProcoreDesign &&
            (await isPdfOrDrawingObjectId(dataAccess, result.objectId, { knownPdfObjectIds, signal: abortSignal }));
        if (sheetScenePickIsBlocked(pickedSheet)) {
            if (abortSignal.aborted) {
                return;
            }
            // Same as the empty-space path above: do not leave an interrupted click's
            // optimistic fragment painted with its snapshot pending.
            if (pendingPaint && getPendingOptimisticPaint()) {
                dispatch(renderActions.setMainObject(baselineMainObject));
                dispatch(renderActions.setMainObjects(baselineMainObjects));
                dispatchHighlighted(highlightActions.setIds(baselineHighlightIds));
                discardPendingOptimisticPaint();
            }
            dispatch(renderActions.setStamp(null));
            return;
        }

        if (abortSignal.aborted) {
            return;
        }

        if (selectIsXRayActive(store.getState())) {
            // Isolation landed while the PDF check ran; leave its selection alone.
            discardPendingOptimisticPaint();
            return;
        }

        // Instant highlight of the picked mesh (zero I/O), then expand to the
        // resolved parent + descendants below. Re-clicks of an already-highlighted
        // mesh return above, so this path is always a new object.
        const paintedOptimistically = !isMultipleSelectionActive;
        if (paintedOptimistically) {
            setPendingOptimisticPaint({
                ownerGeneration: stampGeneration,
                previousMainObject: baselineMainObject,
                previousMainObjects: baselineMainObjects,
                previousHighlightIds: Array.from(baselineHighlightIds),
            });
            dispatch(renderActions.setMainObject(result.objectId));
            dispatch(renderActions.setMainObjects([result.objectId]));
            dispatchHighlighted(highlightActions.setIds([result.objectId]));
        }

        const restoreOptimisticPaintIfCurrent = () => {
            if (isCurrentOptimisticPaintOwner(stampGeneration)) {
                const currentPaint = getPendingOptimisticPaint();
                if (currentPaint) {
                    dispatch(renderActions.setMainObject(currentPaint.previousMainObject));
                    dispatch(renderActions.setMainObjects(currentPaint.previousMainObjects));
                    dispatchHighlighted(highlightActions.setIds(currentPaint.previousHighlightIds));
                    discardPendingOptimisticPaint();
                }
            }
        };

        let mainObjectId = result.objectId;
        let highlightIds: SelectionTarget["highlightIds"] = new Uint32Array([result.objectId]);

        if (selectionTargetPromise) {
            // Do not open the menu until the resolved highlight set is known.
            // An early stamp would show single-object actions (hide similar,
            // section plane) then swap to the multi-object menu once descendants
            // are added — and those early items would be clickable in between.
            dispatch(renderActions.setStamp(null));

            const selectionTarget = await selectionTargetPromise;

            if (abortSignal.aborted) {
                restoreOptimisticPaintIfCurrent();
                return;
            }

            mainObjectId = selectionTarget.mainObjectId;
            highlightIds = selectionTarget.highlightIds;
        }

        if (selectIsXRayActive(store.getState())) {
            // Isolation landed mid-resolve and owns the highlight set now, so neither the
            // resolved target nor this click's pre-paint baseline may be written over it.
            discardPendingOptimisticPaint();
            return;
        }

        const newStamp = buildStampForResult(pos, result, mainObjectId);
        if (!newStamp) {
            return;
        }

        // Right-click must not deselect. Treat the pick as already selected when
        // either the resolved main object or the raw hit is in the highlight set
        // (First object may highlight the parent while the pick is a descendant).
        const alreadySelected = baselineHighlightSet.has(mainObjectId) || baselineHighlightSet.has(result.objectId);
        if (alreadySelected) {
            if (paintedOptimistically) {
                dispatch(renderActions.setMainObject(baselineMainObject));
                dispatch(renderActions.setMainObjects(baselineMainObjects));
                dispatchHighlighted(highlightActions.setIds(baselineHighlightIds));
            }
            discardPendingOptimisticPaint();
            dispatch(renderActions.setStamp(newStamp));
            return;
        }

        discardPendingOptimisticPaint();

        if (isMultipleSelectionActive) {
            const selectObjectNoStamp = () => {
                dispatch(renderActions.setMainObject(mainObjectId));
                dispatch(renderActions.addToMainObjects([mainObjectId]));
                dispatchHighlighted(highlightActions.add(highlightIds));
            };
            const selectObject = () => {
                selectObjectNoStamp();
                dispatch(renderActions.setStamp(newStamp));
            };
            selectObject();

            dispatch(
                undoRedoActions.push({
                    description: "Context Menu Multiselect object",
                    undoCallback: () => {
                        dispatch(renderActions.setMainObject(baselineMainObject));
                        dispatch(renderActions.removeFromMainObjects([mainObjectId]));
                        dispatchHighlighted(highlightActions.remove(highlightIds));
                    },
                    redoCallback: selectObjectNoStamp,
                }),
            );
            return;
        }

        const selectObjectNoStamp = () => {
            dispatch(renderActions.setMainObject(mainObjectId));
            dispatch(renderActions.setMainObjects([mainObjectId]));
            dispatchHighlighted(highlightActions.setIds(highlightIds));
        };
        const selectObject = () => {
            selectObjectNoStamp();
            dispatch(renderActions.setStamp(newStamp));
        };

        // Optimistic paint already selected the raw pick. Skip the second
        // highlight dispatch when resolve did not expand the set.
        const finalMatchesInstantPaint =
            paintedOptimistically && mainObjectId === result.objectId && highlightIds.length === 1;
        if (finalMatchesInstantPaint) {
            dispatch(renderActions.setStamp(newStamp));
        } else {
            selectObject();
        }

        dispatch(
            undoRedoActions.push({
                description: "Context Menu Select object",
                undoCallback: () => {
                    dispatch(renderActions.setMainObject(baselineMainObject));
                    dispatch(renderActions.setMainObjects(baselineMainObjects));
                    dispatchHighlighted(highlightActions.setIds(baselineHighlightIds));
                },
                redoCallback: selectObjectNoStamp,
            }),
        );
    };

    return { handleContextMenu, showContextMenuStamp };
}
